import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/ApiError';
import { DEFAULT_LIMIT, fetchRecommendationsLive } from '../api/client';
import type { QueueUpdate, RecommendResponse, Source } from '../api/types';

/**
 * The whole page is one of four states. Modelling it as a discriminated union
 * rather than a bag of booleans means "loading and error at the same time" is
 * unrepresentable, and every render path knows exactly which data it has.
 */
export type RequestState =
  | { status: 'idle' }
  | { status: 'loading'; username: string; source: Source; queue: QueueUpdate | null }
  | { status: 'success'; username: string; source: Source; data: RecommendResponse }
  | { status: 'error'; username: string; source: Source; error: ApiError };

export interface UseRecommendations {
  state: RequestState;
  submit: (username: string, source: Source) => void;
  reset: () => void;
}

export function useRecommendations(limit: number = DEFAULT_LIMIT): UseRecommendations {
  const [state, setState] = useState<RequestState>({ status: 'idle' });

  // A second submit while one is in flight must not let the first response win.
  const inFlight = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      inFlight.current?.abort();
    };
  }, []);

  const submit = useCallback(
    (rawUsername: string, source: Source) => {
      const username = rawUsername.trim();
      if (username === '') return;

      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;

      setState({ status: 'loading', username, source, queue: null });

      const onQueue = (queue: QueueUpdate) => {
        if (!mounted.current || controller.signal.aborted) return;
        setState((s) => (s.status === 'loading' ? { ...s, queue } : s));
      };

      fetchRecommendationsLive({ username, source, limit }, onQueue, controller.signal)
        .then((data) => {
          if (!mounted.current || controller.signal.aborted) return;
          setState({ status: 'success', username, source, data });
        })
        .catch((cause: unknown) => {
          if (!mounted.current || controller.signal.aborted) return;
          const error =
            cause instanceof ApiError ? cause : new ApiError('unknown_error');
          setState({ status: 'error', username, source, error });
        });
    },
    [limit],
  );

  const reset = useCallback(() => {
    inFlight.current?.abort();
    setState({ status: 'idle' });
  }, []);

  return { state, submit, reset };
}
